import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useQuery } from '@tanstack/react-query'
import { consumosApi } from '@/services/api'
import { fmt, parseDate } from '@/utils/planningFormatters'
import { useFmtArticulo } from '@/hooks/useFmtArticulo'

/**
 * Lo mínimo que el panel necesita saber de una variante: los cuatro parámetros
 * de la consulta de disponibilidad más lo pendiente y el nombre para la cabecera.
 * Es estructural a propósito: una fila del Gantt (`PlanificacionSemanalRow`) lo
 * cumple tal cual y una línea de carga se mapea a él.
 */
export interface SlideOverComp {
  itemid: string
  inventcolorid?: string | null
  inventstyleid?: string | null
  dataareaid?: string | null
  pendiente: number
  nombreMaterial?: string | null
}

export function SlideOver({ comp, dark, onClose, mostrarSustitutivo = true }: {
  comp: SlideOverComp
  dark: boolean
  onClose: () => void
  /** false donde sustituir no tiene sentido (p. ej. un producto ya vendido). */
  mostrarSustitutivo?: boolean
}) {
  const { t, i18n } = useTranslation()
  const locale = i18n.resolvedLanguage || 'es'
  const fmtArt = useFmtArticulo()

  const [showSubstitute, setShowSubstitute] = useState(false)

  const TIPO_CFG: Record<string, { label: string; badgeDark: string; badgeLight: string }> = {
    compra:        { label: t('planning.slideOver.typePurchase'), badgeDark: 'bg-sky-500/15 text-sky-400',          badgeLight: 'bg-sky-50 text-sky-600 border border-sky-200' },
    produccion:    { label: t('planning.slideOver.typeProd'),     badgeDark: 'bg-emerald-500/15 text-emerald-400',  badgeLight: 'bg-emerald-50 text-emerald-700 border border-emerald-200' },
    transferencia: { label: t('planning.slideOver.typeTransfer'), badgeDark: 'bg-amber-500/15 text-amber-400',      badgeLight: 'bg-amber-50 text-amber-700 border border-amber-200' },
  }

  // ── Primary material entries ───────────────────────────────────────────────
  const entradasQ = useQuery({
    queryKey: ['entradas-compra', comp.itemid, comp.inventcolorid ?? null, comp.inventstyleid ?? null, comp.dataareaid ?? null],
    queryFn: () => consumosApi.getEntradasCompra({
      itemid: comp.itemid,
      inventcolorid: comp.inventcolorid ?? undefined,
      inventstyleid: comp.inventstyleid ?? undefined,
      dataareaid: comp.dataareaid ?? undefined,
    }),
  })

  // ── Substitute entries (fetched on demand) ────────────────────────────────
  const varianteQ = useQuery({
    queryKey: ['entradas-variante', comp.itemid, comp.inventcolorid ?? null, comp.inventstyleid ?? null, comp.dataareaid ?? null],
    queryFn: () => consumosApi.getEntradasVariante({
      itemid: comp.itemid,
      inventcolorid: comp.inventcolorid ?? undefined,
      inventstyleid: comp.inventstyleid ?? undefined,
      dataareaid: comp.dataareaid ?? undefined,
    }),
    enabled: showSubstitute,
  })

  // ── Derived — primary ─────────────────────────────────────────────────────
  const allRows       = entradasQ.data?.data ?? []
  const stock         = allRows.filter(e => e.tipo === 'disponibilidad')
  const futuras       = allRows
    .filter(e => e.tipo !== 'disponibilidad')
    .sort((a, b) => (a.fechaEntrada ?? '9').localeCompare(b.fechaEntrada ?? '9'))
  const totalStock    = stock.reduce((s, e) => s + e.qtyEntrada, 0)
  const totalFisico   = stock.reduce((s, e) => s + e.qtyFisico, 0)
  const totalEntradas = futuras.reduce((s, e) => s + e.qtyEntrada, 0)
  const pendiente     = Math.max(0, comp.pendiente)

  // ── Derived — substitute ──────────────────────────────────────────────────
  const varAllRows    = varianteQ.data?.data ?? []
  const varStock      = varAllRows.filter(e => e.tipo === 'disponibilidad')
  const varFuturas    = varAllRows
    .filter(e => e.tipo !== 'disponibilidad')
    .sort((a, b) => (a.fechaEntrada ?? '9').localeCompare(b.fechaEntrada ?? '9'))
  const totalVarStock = varStock.reduce((s, e) => s + e.qtyEntrada, 0)
  const totalVarFut   = varFuturas.reduce((s, e) => s + e.qtyEntrada, 0)
  const varHasData    = varAllRows.length > 0
  const varFetched    = showSubstitute && varianteQ.isSuccess

  // Substitute identity (nombre is the same across all rows — article-level field)
  const varItemId  = varHasData ? (varAllRows[0]?.itemid   ?? '') : ''
  const varNombre  = varHasData ? (varAllRows[0]?.nombre   ?? '') : ''
  const varCompany = varHasData ? (varAllRows[0]?.dataareaid ?? '') : ''
  const varColor   = varHasData ? (varAllRows[0]?.inventcolorid ?? '') : ''
  const varStyle   = varHasData ? (varAllRows[0]?.inventstyleid ?? '') : ''

  // ── Stacked coverage calculation ──────────────────────────────────────────
  const primaryPct = pendiente > 0 ? Math.min(100, Math.round((totalEntradas / pendiente) * 100)) : 100
  // Substitute contributes only up to the remaining gap after primary
  const varRawPct  = varFetched && pendiente > 0 ? Math.round((totalVarFut / pendiente) * 100) : 0
  const varPct     = Math.max(0, Math.min(100 - primaryPct, varRawPct))
  const combinedPct = primaryPct + varPct
  const isCovered  = combinedPct >= 100
  // True when primary alone wouldn't cover but combined does
  const needsSubstitute = isCovered && primaryPct < 100

  // Color of the coverage % text
  const pctColor = isCovered
    ? needsSubstitute
      ? (dark ? 'text-violet-400' : 'text-violet-600')
      : (dark ? 'text-emerald-400' : 'text-emerald-600')
    : combinedPct >= 50
      ? (dark ? 'text-yellow-400' : 'text-yellow-600')
      : (dark ? 'text-red-400' : 'text-red-600')

  // Primary bar color (based on primary coverage alone, not combined)
  const primaryBarColor = primaryPct >= 100
    ? 'bg-emerald-400'
    : primaryPct >= 50 ? 'bg-yellow-400' : 'bg-red-400'

  function fmtFecha(s?: string): string {
    const d = parseDate(s)
    if (!d) return '—'
    return d.toLocaleDateString(locale, { day: 'numeric', month: 'short', year: 'numeric' })
  }

  // ── Theme shortcuts ───────────────────────────────────────────────────────
  const bg      = dark ? 'bg-slate-900'    : 'bg-white'
  const bdr     = dark ? 'border-white/10' : 'border-slate-200'
  const txtM    = dark ? 'text-slate-50'   : 'text-slate-900'
  const txtMut  = dark ? 'text-slate-400'  : 'text-slate-500'
  const thCls   = dark ? 'text-slate-500'  : 'text-slate-400'
  const cellCls = dark ? 'text-slate-300'  : 'text-slate-700'
  const rowHov  = dark ? 'hover:bg-white/3' : 'hover:bg-slate-50'
  const rowBdr  = dark ? 'border-white/5'  : 'border-slate-100'

  // ── Matiz de la fecha de entrada ──────────────────────────────────────────
  // prod_entradas devuelve tipo_fecha sólo en las filas de compra: dice si la
  // fecha es la confirmada por el proveedor (a nivel de línea o de cabecera del
  // pedido) o la solicitada todavía sin confirmar. Los literales vienen del SP
  // en castellano; se traducen y, si aparece un valor nuevo, se pinta crudo.
  const TIPO_FECHA_KEY: Record<string, string> = {
    'Sin confirmar':       'dateUnconfirmed',
    'Confirmado línea':    'dateConfirmedLine',
    'Confirmado cabecera': 'dateConfirmedHeader',
  }

  function tipoFechaInfo(raw?: string): { label: string; cls: string } | null {
    const v = (raw ?? '').trim()
    if (!v) return null
    const key = TIPO_FECHA_KEY[v]
    return {
      label: key ? t(`planning.slideOver.${key}`) : v,
      cls: v === 'Sin confirmar'
        ? (dark ? 'text-amber-400' : 'text-amber-600')
        : txtMut,
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex justify-end">
      <div className="absolute inset-0 bg-black/40 backdrop-blur-[2px]" onClick={onClose} />
      <div className={`relative z-10 w-[480px] max-w-[92vw] h-full flex flex-col shadow-2xl border-l ${bg} ${bdr}`}>

        {/* ── Header ─────────────────────────────────────────────────────── */}
        <div className={`flex items-start justify-between gap-3 px-5 py-4 border-b ${bdr} shrink-0`}>
          <div className="min-w-0">
            <div className={`text-[10px] font-bold uppercase tracking-widest ${txtMut} mb-1.5`}>{t('planning.slideOver.materialStatus')}</div>
            <div className="flex items-center gap-1.5 flex-wrap">
              <span className={`text-[16px] font-black ${txtM}`}>{fmtArt(comp.itemid)}</span>
              {[comp.inventcolorid, comp.inventstyleid].filter(Boolean).map((dim, di) => (
                <span key={di} className={`text-[11px] font-semibold px-1.5 py-0.5 rounded ${
                  dark ? 'bg-white/10 text-slate-300' : 'bg-slate-100 text-slate-600 border border-slate-200'
                }`}>{dim}</span>
              ))}
            </div>
            {comp.nombreMaterial && (
              <div className={`text-[11px] ${txtMut} mt-0.5 leading-snug max-w-[360px]`}>{comp.nombreMaterial}</div>
            )}
          </div>
          <button onClick={onClose}
            className={`shrink-0 mt-0.5 w-7 h-7 flex items-center justify-center rounded-lg transition-colors cursor-pointer ${
              dark ? 'text-slate-400 hover:bg-white/10 hover:text-slate-100'
                   : 'text-slate-400 hover:bg-slate-100 hover:text-slate-700'
            }`}
            aria-label={t('planning.slideOver.closeAriaLabel')}
          >
            <svg className="w-3.5 h-3.5" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
              <path d="M1 1l12 12M13 1L1 13"/>
            </svg>
          </button>
        </div>

        {/* ── Stock actual (primary) ───────────────────────────────────────── */}
        {(entradasQ.isLoading || stock.length > 0) && (
          <div className={`px-5 py-4 border-b ${bdr} shrink-0`}>
            <div className={`text-[10px] font-bold uppercase tracking-widest ${txtMut} mb-3`}>{t('planning.slideOver.currentStock')}</div>
            {entradasQ.isLoading ? (
              <div className={`text-[11px] ${txtMut}`}>{t('planning.slideOver.loading')}</div>
            ) : (
              <table className="w-full text-[11px]">
                <thead>
                  <tr className={`border-b ${rowBdr}`}>
                    <th className={`pb-1.5 text-[9px] font-bold uppercase tracking-wider text-left ${thCls}`}>{t('planning.slideOver.company')}</th>
                    <th className={`pb-1.5 text-[9px] font-bold uppercase tracking-wider text-left ${thCls}`}>{t('planning.slideOver.warehouse')}</th>
                    <th className={`pb-1.5 text-[9px] font-bold uppercase tracking-wider text-right ${thCls}`}>{t('planning.slideOver.physical')}</th>
                    <th className={`pb-1.5 text-[9px] font-bold uppercase tracking-wider text-right ${thCls}`}>{t('planning.slideOver.available')}</th>
                  </tr>
                </thead>
                <tbody>
                  {stock.map((s, i) => (
                    <tr key={i} className={`border-b last:border-0 ${rowBdr}`}>
                      <td className={`py-1.5 font-mono text-[10px] ${txtMut}`}>{s.dataareaid}</td>
                      <td className={`py-1.5 font-mono text-[10px] ${cellCls}`}>{s.inventlocationid ?? '—'}</td>
                      <td className={`py-1.5 text-right tabular-nums ${cellCls}`}>{fmt(s.qtyFisico, 2)}</td>
                      <td className={`py-1.5 text-right tabular-nums font-bold ${
                        s.qtyEntrada > 0 ? (dark ? 'text-emerald-400' : 'text-emerald-600') : (dark ? 'text-slate-400' : 'text-slate-500')
                      }`}>{fmt(s.qtyEntrada, 2)}</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className={`border-t-2 ${dark ? 'border-white/15' : 'border-slate-200'}`}>
                    <td colSpan={2} className={`pt-2 text-[10px] font-bold uppercase tracking-wide ${txtMut}`}>{t('planning.slideOver.totalStock')}</td>
                    <td className={`pt-2 text-right tabular-nums font-bold text-[12px] ${cellCls}`}>{fmt(totalFisico, 2)}</td>
                    <td className={`pt-2 text-right tabular-nums font-black text-[13px] ${
                      totalStock > 0 ? (dark ? 'text-emerald-400' : 'text-emerald-600') : (dark ? 'text-slate-400' : 'text-slate-500')
                    }`}>{fmt(totalStock, 2)}</td>
                  </tr>
                </tfoot>
              </table>
            )}
          </div>
        )}

        {/* ── Cobertura del déficit ── stacked bar ────────────────────────── */}
        <div className={`px-5 py-4 border-b ${bdr} shrink-0`}>
          <div className={`text-[10px] font-bold uppercase tracking-widest ${txtMut} mb-3`}>{t('planning.slideOver.deficitCoverage')}</div>

          {/* Numbers row */}
          <div className="flex items-end justify-between gap-4 mb-3">
            <div>
              <div className={`text-[10px] ${txtMut} mb-0.5`}>{t('planning.slideOver.pendingDeficit')}</div>
              <div className={`text-[22px] font-black tabular-nums leading-none ${
                pendiente > 0 ? (dark ? 'text-red-400' : 'text-red-600') : (dark ? 'text-emerald-400' : 'text-emerald-600')
              }`}>
                {pendiente > 0 ? `+${fmt(pendiente, 2)}` : fmt(pendiente, 2)}
                <span className={`text-[12px] font-medium ml-1 ${txtMut}`}>u.</span>
              </div>
            </div>
            <div className="text-right">
              <div className={`text-[10px] ${txtMut} mb-0.5`}>{t('planning.slideOver.totalExpected')}</div>
              <div className={`text-[22px] font-black tabular-nums leading-none ${dark ? 'text-sky-400' : 'text-sky-600'}`}>
                {entradasQ.isLoading ? '…' : fmt(totalEntradas, 2)}
                <span className={`text-[12px] font-medium ml-1 ${txtMut}`}>u.</span>
              </div>
            </div>
          </div>

          {/* Stacked coverage bar */}
          <div className={`relative h-2.5 rounded-full overflow-hidden mb-2 ${dark ? 'bg-slate-700' : 'bg-slate-100'}`}>
            {/* Substitute segment (rendered first, visible in the gap after primary) */}
            {varPct > 0 && (
              <div
                className="absolute inset-y-0 bg-violet-400 transition-all duration-700"
                style={{ left: `${primaryPct}%`, width: `${varPct}%` }}
              />
            )}
            {/* Primary segment (on top) */}
            <div
              className={`absolute inset-y-0 left-0 transition-all duration-700 ${primaryBarColor}`}
              style={{ width: `${entradasQ.isLoading ? 0 : primaryPct}%` }}
            />
          </div>

          {/* Bar legend */}
          <div className="flex items-start justify-between gap-2">
            <div className="flex flex-col gap-0.5">
              {pendiente === 0 ? (
                <span className={`text-[10px] ${txtMut}`}>{t('planning.slideOver.noDeficit')}</span>
              ) : (
                <>
                  <div className="flex items-center gap-1.5">
                    <span className={`inline-block w-2 h-2 rounded-sm shrink-0 ${primaryBarColor}`} />
                    <span className={`text-[10px] ${txtMut}`}>
                      {t('planning.slideOver.expectedEntries', { count: futuras.length })}
                      <span className="ml-1 tabular-nums font-semibold">{primaryPct}%</span>
                    </span>
                  </div>
                  {varPct > 0 && (
                    <div className="flex items-center gap-1.5">
                      <span className="inline-block w-2 h-2 rounded-sm shrink-0 bg-violet-400" />
                      <span className={`text-[10px] ${dark ? 'text-violet-400' : 'text-violet-600'}`}>
                        {t('planning.slideOver.substituteContrib', { count: varFuturas.length })}
                        <span className="ml-1 tabular-nums font-semibold">+{varPct}%</span>
                      </span>
                    </div>
                  )}
                </>
              )}
            </div>
            {/* Combined % badge */}
            <div className={`text-right shrink-0`}>
              {!entradasQ.isLoading && (
                <>
                  <div className={`text-[18px] font-black tabular-nums leading-none ${pctColor}`}>
                    {combinedPct}%
                  </div>
                  {varPct > 0 && (
                    <div className={`text-[9px] font-semibold mt-0.5 ${dark ? 'text-violet-400' : 'text-violet-600'}`}>
                      {t('planning.slideOver.combinedCoverage')}
                    </div>
                  )}
                </>
              )}
              {entradasQ.isLoading && <span className={`text-[18px] font-black ${txtMut}`}>…</span>}
            </div>
          </div>
        </div>

        {/* ── Próximas entradas + Sustitutivo (scrollable) ─────────────────── */}
        <div className="flex-1 overflow-y-auto min-h-0">

          {/* Próximas entradas — sticky header */}
          <div className={`flex items-center justify-between px-5 py-3 sticky top-0 z-10 border-b ${bg} ${bdr}`}>
            <span className={`text-[10px] font-bold uppercase tracking-widest ${txtMut}`}>{t('planning.slideOver.upcomingEntries')}</span>
            {!entradasQ.isLoading && (
              <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded-full tabular-nums ${
                dark ? 'bg-white/10 text-slate-400' : 'bg-slate-100 text-slate-500'
              }`}>{futuras.length}</span>
            )}
          </div>

          {entradasQ.isLoading && (
            <div className={`py-14 text-center text-[12px] ${txtMut}`}>{t('planning.slideOver.loading')}</div>
          )}

          {!entradasQ.isLoading && futuras.length === 0 && (
            <div className={`py-10 text-center ${txtMut}`}>
              <div className="text-[28px] mb-2 opacity-20">—</div>
              <div className="text-[12px]">{t('planning.slideOver.noEntries')}</div>
            </div>
          )}

          {!entradasQ.isLoading && futuras.length > 0 && (
            <table className="w-full text-[11px]">
              <thead>
                <tr className={`border-b ${rowBdr}`}>
                  <th className={`px-4 py-2.5 text-[9px] font-bold uppercase tracking-wider text-left ${thCls}`}>{t('planning.slideOver.type')}</th>
                  <th className={`px-4 py-2.5 text-[9px] font-bold uppercase tracking-wider text-left ${thCls}`}>{t('planning.slideOver.reference')}</th>
                  <th className={`px-4 py-2.5 text-[9px] font-bold uppercase tracking-wider text-left ${thCls}`}>{t('planning.slideOver.warehouse')}</th>
                  <th className={`px-4 py-2.5 text-[9px] font-bold uppercase tracking-wider text-left ${thCls}`}>{t('planning.slideOver.date')}</th>
                  <th className={`px-4 py-2.5 text-[9px] font-bold uppercase tracking-wider text-right ${thCls}`}>{t('planning.slideOver.qty')}</th>
                </tr>
              </thead>
              <tbody>
                {futuras.map((e, i) => {
                  const cfg     = TIPO_CFG[e.tipo] ?? TIPO_CFG.compra
                  const almacen = e.tipo === 'transferencia'
                    ? `${e.inventlocationid ?? '?'} → ${e.inventlocationto ?? '?'}`
                    : (e.inventlocationid ?? '—')
                  const tf      = tipoFechaInfo(e.tipoFecha)
                  return (
                    <tr key={i} className={`border-b last:border-0 ${rowBdr} transition-colors ${rowHov}`}>
                      <td className="px-4 py-2.5">
                        <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded uppercase tracking-wide ${dark ? cfg.badgeDark : cfg.badgeLight}`}>
                          {cfg.label}
                        </span>
                      </td>
                      <td className={`px-4 py-2.5 font-mono font-semibold text-[10px] ${dark ? 'text-slate-300' : 'text-slate-700'}`}>{e.purchid}</td>
                      <td className={`px-4 py-2.5 text-[10px] ${txtMut}`}>{almacen}</td>
                      <td className={`px-4 py-2.5 ${cellCls} whitespace-nowrap`}>
                        <div>{fmtFecha(e.fechaEntrada)}</div>
                        {tf && <div className={`text-[9px] leading-tight ${tf.cls}`}>{tf.label}</div>}
                      </td>
                      <td className={`px-4 py-2.5 text-right tabular-nums font-bold ${dark ? 'text-emerald-400' : 'text-emerald-600'}`}>
                        +{fmt(e.qtyEntrada, 2)}
                      </td>
                    </tr>
                  )
                })}
              </tbody>
              <tfoot>
                <tr className={`border-t-2 ${dark ? 'border-white/15' : 'border-slate-200'}`}>
                  <td colSpan={4} className={`px-4 py-2.5 text-[10px] font-bold uppercase tracking-wide ${txtMut}`}>{t('planning.slideOver.totalEntries')}</td>
                  <td className={`px-4 py-2.5 text-right tabular-nums font-black text-[14px] ${dark ? 'text-emerald-400' : 'text-emerald-600'}`}>
                    +{fmt(totalEntradas, 2)}
                  </td>
                </tr>
              </tfoot>
            </table>
          )}

          {/* ── Disponibilidad sustitutivo ────────────────────────────────── */}
          {mostrarSustitutivo && (
          <div className={`border-t-2 ${dark ? 'border-white/10' : 'border-slate-100'}`}>

            {/* Toggle button — shows substitute itemid chip once data is available */}
            <button
              onClick={() => setShowSubstitute(v => !v)}
              className={`w-full flex items-center justify-between gap-3 px-5 py-3.5 text-left transition-colors cursor-pointer ${
                showSubstitute
                  ? dark ? 'bg-violet-500/10 text-violet-300' : 'bg-violet-50 text-violet-700'
                  : dark ? 'text-slate-300 hover:bg-white/5' : 'text-slate-600 hover:bg-slate-50'
              }`}
            >
              <div className="flex items-center gap-2.5 min-w-0">
                {/* swap icon */}
                <svg className="w-4 h-4 shrink-0 opacity-70" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M4 5h12M4 5l3-3M4 5l3 3M16 15H4M16 15l-3-3M16 15l-3 3"/>
                </svg>
                <span className="text-[11px] font-semibold leading-snug truncate">
                  {t('planning.slideOver.viewSubstitute')}
                </span>
                {/* Substitute itemid chip — shown after data loads */}
                {varFetched && varHasData && varItemId && (
                  <span className={`shrink-0 text-[10px] font-bold font-mono px-2 py-0.5 rounded-md ${
                    dark ? 'bg-violet-500/20 text-violet-300 border border-violet-500/30'
                         : 'bg-violet-100 text-violet-700 border border-violet-200'
                  }`}>
                    {fmtArt(varItemId)}
                  </span>
                )}
              </div>
              {/* chevron */}
              <svg
                className={`w-3.5 h-3.5 shrink-0 transition-transform duration-200 ${showSubstitute ? 'rotate-180' : ''}`}
                viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
              >
                <path d="M2 4l4 4 4-4"/>
              </svg>
            </button>

            {/* Substitute panel */}
            {showSubstitute && (
              <div className={`pb-6 ${dark ? 'bg-violet-950/30' : 'bg-violet-50/40'}`}>

                {/* Loading */}
                {varianteQ.isLoading && (
                  <div className={`py-10 text-center text-[12px] ${txtMut}`}>
                    {t('planning.slideOver.substituteLoading')}
                  </div>
                )}

                {/* Error */}
                {varianteQ.isError && (
                  <div className={`mx-5 mt-4 px-4 py-3 rounded-lg text-[11px] ${
                    dark ? 'bg-red-500/10 text-red-400 border border-red-500/20' : 'bg-red-50 text-red-600 border border-red-200'
                  }`}>
                    {t('planning.slideOver.substituteError')}
                  </div>
                )}

                {/* No substitute found */}
                {!varianteQ.isLoading && !varianteQ.isError && !varHasData && (
                  <div className={`py-10 text-center ${txtMut}`}>
                    <div className={`mx-auto mb-3 w-8 h-8 flex items-center justify-center rounded-full ${
                      dark ? 'bg-slate-700/60' : 'bg-slate-200/60'
                    }`}>
                      <svg className="w-4 h-4 opacity-40" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                        <circle cx="8" cy="8" r="6"/><path d="M8 5v3M8 11h.01"/>
                      </svg>
                    </div>
                    <div className="text-[12px]">{t('planning.slideOver.substituteEmpty')}</div>
                  </div>
                )}

                {/* Substitute data */}
                {!varianteQ.isLoading && !varianteQ.isError && varHasData && (
                  <div>

                    {/* Identity header — itemid + nombre + company */}
                    <div className={`mx-5 mt-4 mb-3 px-4 py-3 rounded-xl ${
                      dark ? 'bg-violet-500/10 border border-violet-500/20' : 'bg-white border border-violet-200'
                    }`}>
                      <div className={`text-[9px] font-bold uppercase tracking-widest mb-2 ${
                        dark ? 'text-violet-400' : 'text-violet-500'
                      }`}>
                        {t('planning.slideOver.substituteTitle')}
                      </div>
                      <div className="flex items-start gap-3 min-w-0">
                        <div className={`shrink-0 w-8 h-8 flex items-center justify-center rounded-lg ${
                          dark ? 'bg-violet-500/20' : 'bg-violet-100'
                        }`}>
                          <svg className={`w-4 h-4 ${dark ? 'text-violet-400' : 'text-violet-600'}`} viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M4 5h12M4 5l3-3M4 5l3 3M16 15H4M16 15l-3-3M16 15l-3 3"/>
                          </svg>
                        </div>
                        <div className="min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className={`text-[14px] font-black font-mono ${dark ? 'text-violet-200' : 'text-violet-800'}`}>
                              {fmtArt(varItemId)}
                            </span>
                            {[varColor, varStyle].filter(Boolean).map((dim, di) => (
                              <span key={di} className={`text-[11px] font-semibold px-1.5 py-0.5 rounded ${
                                dark ? 'bg-violet-500/20 text-violet-200' : 'bg-violet-100 text-violet-700 border border-violet-200'
                              }`}>{dim}</span>
                            ))}
                            {varCompany && (
                              <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded ${
                                dark ? 'bg-white/10 text-slate-300' : 'bg-slate-100 text-slate-500 border border-slate-200'
                              }`}>{varCompany}</span>
                            )}
                          </div>
                          {varNombre && (
                            <div className={`text-[11px] mt-0.5 leading-snug ${dark ? 'text-violet-300/80' : 'text-violet-700'}`}>
                              {varNombre}
                            </div>
                          )}
                        </div>
                      </div>
                      {/* What this substitute contributes */}
                      {pendiente > 0 && (
                        <div className={`mt-3 pt-2.5 border-t flex items-center justify-between ${
                          dark ? 'border-violet-500/20' : 'border-violet-200/60'
                        }`}>
                          <span className={`text-[10px] ${dark ? 'text-violet-400' : 'text-violet-500'}`}>
                            {t('planning.slideOver.substituteSubtitle')}
                          </span>
                          <div className="flex items-center gap-1.5">
                            <span className={`text-[12px] font-black tabular-nums ${dark ? 'text-violet-300' : 'text-violet-700'}`}>
                              +{fmt(totalVarFut, 2)} u.
                            </span>
                            <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full ${
                              dark ? 'bg-violet-500/20 text-violet-300' : 'bg-violet-100 text-violet-700'
                            }`}>
                              +{varRawPct}%
                            </span>
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Substitute stock */}
                    {varStock.length > 0 && (
                      <div className={`mx-5 mb-3 rounded-lg overflow-hidden border ${dark ? 'border-white/8' : 'border-violet-200/70'}`}>
                        <div className={`px-3 py-1.5 text-[9px] font-bold uppercase tracking-wider border-b ${rowBdr} ${
                          dark ? 'bg-white/5 text-slate-500' : 'bg-violet-100/70 text-violet-500'
                        }`}>
                          {t('planning.slideOver.currentStock')}
                        </div>
                        <table className="w-full text-[11px]">
                          <thead>
                            <tr className={`border-b ${rowBdr}`}>
                              <th className={`px-3 py-2 text-[9px] font-bold uppercase tracking-wider text-left ${thCls}`}>{t('planning.slideOver.company')}</th>
                              <th className={`px-3 py-2 text-[9px] font-bold uppercase tracking-wider text-left ${thCls}`}>{t('planning.slideOver.warehouse')}</th>
                              <th className={`px-3 py-2 text-[9px] font-bold uppercase tracking-wider text-right ${thCls}`}>{t('planning.slideOver.available')}</th>
                            </tr>
                          </thead>
                          <tbody>
                            {varStock.map((s, i) => (
                              <tr key={i} className={`border-b last:border-0 ${rowBdr}`}>
                                <td className={`px-3 py-1.5 font-mono text-[10px] ${txtMut}`}>{s.dataareaid}</td>
                                <td className={`px-3 py-1.5 font-mono text-[10px] ${cellCls}`}>{s.inventlocationid ?? '—'}</td>
                                <td className={`px-3 py-1.5 text-right tabular-nums font-bold ${
                                  s.qtyEntrada > 0 ? (dark ? 'text-emerald-400' : 'text-emerald-600') : (dark ? 'text-slate-400' : 'text-slate-500')
                                }`}>{fmt(s.qtyEntrada, 2)}</td>
                              </tr>
                            ))}
                          </tbody>
                          <tfoot>
                            <tr className={`border-t-2 ${dark ? 'border-white/15' : 'border-slate-200'}`}>
                              <td colSpan={2} className={`px-3 py-2 text-[10px] font-bold uppercase tracking-wide ${txtMut}`}>{t('planning.slideOver.totalStock')}</td>
                              <td className={`px-3 py-2 text-right tabular-nums font-black text-[12px] ${
                                totalVarStock > 0 ? (dark ? 'text-emerald-400' : 'text-emerald-600') : (dark ? 'text-slate-400' : 'text-slate-500')
                              }`}>{fmt(totalVarStock, 2)}</td>
                            </tr>
                          </tfoot>
                        </table>
                      </div>
                    )}

                    {/* Substitute upcoming entries */}
                    {varFuturas.length > 0 && (
                      <div className={`mx-5 rounded-lg overflow-hidden border ${dark ? 'border-white/8' : 'border-violet-200/70'}`}>
                        <div className={`flex items-center justify-between px-3 py-1.5 border-b ${rowBdr} text-[9px] font-bold uppercase tracking-wider ${
                          dark ? 'bg-white/5 text-slate-500' : 'bg-violet-100/70 text-violet-500'
                        }`}>
                          <span>{t('planning.slideOver.upcomingEntries')}</span>
                          <span className={`tabular-nums px-1.5 py-0.5 rounded-full ${
                            dark ? 'bg-white/10 text-slate-400' : 'bg-violet-200/60 text-violet-600'
                          }`}>{varFuturas.length}</span>
                        </div>
                        <table className="w-full text-[11px]">
                          <thead>
                            <tr className={`border-b ${rowBdr}`}>
                              <th className={`px-3 py-2 text-[9px] font-bold uppercase tracking-wider text-left ${thCls}`}>{t('planning.slideOver.type')}</th>
                              <th className={`px-3 py-2 text-[9px] font-bold uppercase tracking-wider text-left ${thCls}`}>{t('planning.slideOver.reference')}</th>
                              <th className={`px-3 py-2 text-[9px] font-bold uppercase tracking-wider text-left ${thCls}`}>{t('planning.slideOver.warehouse')}</th>
                              <th className={`px-3 py-2 text-[9px] font-bold uppercase tracking-wider text-left ${thCls}`}>{t('planning.slideOver.date')}</th>
                              <th className={`px-3 py-2 text-[9px] font-bold uppercase tracking-wider text-right ${thCls}`}>{t('planning.slideOver.qty')}</th>
                            </tr>
                          </thead>
                          <tbody>
                            {varFuturas.map((e, i) => {
                              const cfg     = TIPO_CFG[e.tipo] ?? TIPO_CFG.compra
                              const almacen = e.tipo === 'transferencia'
                                ? `${e.inventlocationid ?? '?'} → ${e.inventlocationto ?? '?'}`
                                : (e.inventlocationid ?? '—')
                              const tf      = tipoFechaInfo(e.tipoFecha)
                              return (
                                <tr key={i} className={`border-b last:border-0 ${rowBdr} transition-colors ${rowHov}`}>
                                  <td className="px-3 py-2">
                                    <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded uppercase tracking-wide ${dark ? cfg.badgeDark : cfg.badgeLight}`}>
                                      {cfg.label}
                                    </span>
                                  </td>
                                  <td className={`px-3 py-2 font-mono font-semibold text-[10px] ${dark ? 'text-slate-300' : 'text-slate-700'}`}>{e.purchid}</td>
                                  <td className={`px-3 py-2 text-[10px] ${txtMut}`}>{almacen}</td>
                                  <td className={`px-3 py-2 ${cellCls} whitespace-nowrap`}>
                                    <div>{fmtFecha(e.fechaEntrada)}</div>
                                    {tf && <div className={`text-[9px] leading-tight ${tf.cls}`}>{tf.label}</div>}
                                  </td>
                                  <td className={`px-3 py-2 text-right tabular-nums font-bold ${dark ? 'text-violet-400' : 'text-violet-600'}`}>
                                    +{fmt(e.qtyEntrada, 2)}
                                  </td>
                                </tr>
                              )
                            })}
                          </tbody>
                          <tfoot>
                            <tr className={`border-t-2 ${dark ? 'border-white/15' : 'border-slate-200'}`}>
                              <td colSpan={4} className={`px-3 py-2.5 text-[10px] font-bold uppercase tracking-wide ${txtMut}`}>{t('planning.slideOver.totalEntries')}</td>
                              <td className={`px-3 py-2.5 text-right tabular-nums font-black text-[13px] ${dark ? 'text-violet-400' : 'text-violet-600'}`}>
                                +{fmt(totalVarFut, 2)}
                              </td>
                            </tr>
                          </tfoot>
                        </table>
                      </div>
                    )}

                  </div>
                )}
              </div>
            )}
          </div>
          )}

        </div>
      </div>
    </div>
  )
}
